import { forwardRef, type HTMLAttributes, type ReactElement, type ReactNode } from 'react'
import { twMerge } from 'tailwind-merge'
import { IconBadge } from '@/components/base/IconBadge'
import { CardFrame } from '@/components/base/CardFrame'
import { type RewardTag } from '@/components/composite/RewardTag'

export interface RewardNotificationProps extends HTMLAttributes<HTMLDivElement> {
  icon: ReactNode
  title: string
  subtitle?: string
  accentColor?: 'none' | 'yellow' | 'green' | 'blue' | 'pink'
  rewardTag?: ReactElement<typeof RewardTag>
}

export const RewardNotification = forwardRef<HTMLDivElement, RewardNotificationProps>(
  ({ icon, title, subtitle, rewardTag, accentColor = 'none', className, ...props }, ref) => {
    return (
      <div
        className={twMerge(
          'flex justify-between items-center',
          'w-full max-w-[500px]',
          'gap-2 px-2 pb-1 pt-[7px]',
          'bg-primary-background shadow-light-top-accent-deep-shadow rounded-10',
          className
        )}
        ref={ref}
        {...props}
      >
        <div className="flex items-center gap-2">
          <IconBadge accentColor={accentColor}>{icon}</IconBadge>

          <div className="relative -top-[1px] flex flex-col flex-shrink-0 justify-center font-bold">
            <span className=" text-yellow font-formula text-base [text-shadow:0px_1px_1px_rgba(0,0,0,0.25)]">
              {title}
            </span>

            {subtitle ? <span className="text-sm">{subtitle}</span> : null}
          </div>
        </div>

        {rewardTag ? <CardFrame className="p-[2px]">{rewardTag}</CardFrame> : null}
      </div>
    )
  }
)

RewardNotification.displayName = 'RewardNotification'
